/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Badge, Card } from '@nexigendigital/nxui'
import { serviceAPI } from 'api/service'
import { useCreateCartMutation, useProductQuery } from 'api/shop'
import { Addon } from 'models/service'
import { getServiceStatusColour } from 'helpers/utils'

import AddonPurchaseLightbox from 'containers/services/lightbox/addonPurchaseLightbox'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Addons() {
    const location = useLocation()
    const id = +location.pathname?.split('/')[2]
    const { isLoading, isError, data } = serviceAPI.endpoints.service.useQueryState(id)
    const { data: product, isLoading: isProductLoading } = useProductQuery(data?.product.id, { skip: !data?.product.id })
    const [lightbox, setLightbox] = useState({
        status: false,
        data: {
            id: 0
        }
    })
    const [createCart, { data: cart, isSuccess: isCreateCartSuccess }] = useCreateCartMutation({ fixedCacheKey: 'addon-purchase-cart' })
    const columns = useMemo(() => ([
        {
            Header: 'Addons',
            accessor: 'addons'
        },
        {
            Header: 'Description',
            accessor: 'description'
        },
        {
            Header: '',
            accessor: 'action',
            align: 'right'
        }
    ]), [])
    const addonsData = useMemo(() => {
        if (isLoading || isError) {
            return []
        }

        if (data?.addons.length === 0 && product) {
            return product.addons.map((addon) => {
                return {
                    addons: addon.name,
                    description: addon.description,
                    action: (
                        <Anchor
                            color={'primary'}
                            onClick={() => {
                                createCart({
                                    items: [{
                                        name: data.name,
                                        product_id: data.product.id,
                                        service_id: data.id,
                                        order_type: 'all',
                                        billing_cycle_id: data.billing_cycle.id,
                                        addons: [{
                                            product_id: addon.id,
                                            billing_cycle_id: data.billing_cycle.id
                                        }]
                                    }]
                                })
                                setLightbox({
                                    ...lightbox,
                                    data: addon
                                })
                            }}
                        >
                            Add
                        </Anchor>
                    )
                }
            })
        }

        return data?.addons.map((addon: Addon) => {
            return {
                addons: addon.name,
                description: addon.product.description,
                action: <Badge color={getServiceStatusColour(addon.status)}>{addon.status.toUpperCase()}</Badge>
            }
        })
    }, [data?.addons, product?.addons, isLoading, isError])

    useEffect(() => {
        if (isCreateCartSuccess && cart && data) {
            const existingService = cart.items.find(({ service_id }) => service_id === data.id)
            const pendingAddon = existingService?.addons?.find(({ product_id }) => product_id === lightbox.data.id)

            if (pendingAddon) {
                setLightbox({
                    status: true,
                    data: pendingAddon
                })
            }
        }
    }, [isCreateCartSuccess])

    return (
        <>
            <AddonsWrapper
                table={{
                    conditions: {
                        loading: (isLoading || isProductLoading) && 'Loading Addons...',
                        sort: false
                    },
                    render: {
                        columns: () => columns,
                        data: () => addonsData
                    }
                }}
            />
            <AddonPurchaseLightbox
                addon={lightbox.data}
                status={lightbox.status}
                close={() => setLightbox({ ...lightbox, status: false })}
            />
        </>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const AddonsWrapper = styled(Card)`
    width: 100%;
`
